import { autoinject, transient, computedFrom, observable, BindingEngine } from "aurelia-framework";
import { DataType, DataGridColumn, QueryFilter, SourceType } from "../../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import * as Enumerable from "linq";
import { PscInspectionStatusEnum } from "../../../../../models/vetting/vetting.models";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { Reflection } from "../../../../../utilities/reflection";
import { CustomGridPageBase } from "../../../common/custom-grid-page.base";
import { TasksConstants } from "../../../../../models/tasks/task.model";
import { Guid } from "../../../../../utilities/guid";
import KeyDownEvent = JQuery.KeyDownEvent;
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { Claims as CommonUiClaims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { QueriesClientService } from "../../../../../services/queries/queries-client.services";
import { PscActivitiesColumns, PscFilters } from "../../../../../models/pscs/psc.models";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { RightShipMultiLookupModels, MultiLookupModel } from "../../../../components/multi-checked-list-selector/multi-checked-list-selector";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { EntityFinderSearchFilter } from "../../../../components/entity-finder-search-filter/entity-finder-search-filter";
import { cachable } from '@rightship-tech/common-ui/decorators/cachable';

@autoinject()
@transient()
export class PscActivities extends CustomGridPageBase {
    //Add country filter
    public countryEntityTypes: EntityTypeConfigurationModel[];
    public countryEntityFinderFilter: EntityFinderSearchFilter;
    public selectedCountries: number[] = [];
    public showCountryFilter: boolean = false;
    public selectedFilter: { title: string, clear: () => void, apply: () => void };
    public clearCountryOnShow: boolean;

    @computedFrom("selectedCountries", "selectedCountries.length", "countryEntityFinderFilter", "countryEntityFinderFilter.selectedItemIds")
    public get countryFilterActive() {
        return this.selectedCountries?.length > 0 || this.countryEntityFinderFilter?.selectedItemIds?.length > 0;
    }

    public toggleCountryFilters() {
        if (!this.showCountryFilter) {
            this.closeAllFilters();
        }
        this.showCountryFilter = !this.showCountryFilter;
        this.selectedFilter = {
            title: "Country",
            clear: () => {
                this.clearCountryFilter();
            },
            apply: () => {
                this.applyCountryFilter(this.selectedCountries.map(x => {
                    return { id: x }
                }))
            }
        }
    }

    public clearCountryFilter() {
        this.selectedCountries = [];
        this.reloadFilters();
    }

    public applyCountryFilter(items: any[], reload: boolean = true) {
        this.selectedCountries = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.toggleCountryFilters();
    }

    private _isReloading: boolean;

    public portEntityTypes: EntityTypeConfigurationModel[];
    public companiesEntityTypes: EntityTypeConfigurationModel[];

    isMobileFilterOpen: boolean;
    pscInspectionStatuses: LookupValue[];
    public showStatusFilter: boolean = false;
    public showMouFilter: boolean = false;
    public defaultPageCount: number = 100;
    @cachable
    private filters: PscFilters[] = [];
    @cachable
    public statusFilterIds: number[] = [];
    @cachable
    public mouFilterIds: number[] = [];
    public showDetentionFilter: boolean = false;
    @cachable
    public detentionFilterIds: number[] = [];
    public detentionFilterValues: any[] = [{ name: "Yes", id: 1 }, { name: "No", id: 2 }];
    public allPscInspectionStatuses: LookupValue[];
    @cachable
    public rightshipMember: boolean = false;
    public rightShipUser: boolean;
    public canViewSourceColumnInTasksPSC: boolean;
    public isCustomer: boolean = false;
    public myPscsCount: number = 0;
    public rightShipMemberAndPriorityCount: number = 0;
    public showDateFilter: boolean = false;
    @cachable
    public startDate: Date;
    @cachable
    public endDate: Date;
    public discrepancyCount: number = 0;
    public notInSafetyScoreCount: number = 0;
    public rightShipMemberCount: number = 0;
    public pendingVetCount: number = 0;
    public vettedLast12MthsCount: number = 0;
    public dataSources: any[] = [
        { code: "IHS", id: 1, name: "S&P Global" },
        { code: "NYK", id: 2, name: "NYK" },
        { code: "RSM", id: 3, name: "RSM" },
        { code: "RSDS", id: 4, name: "RSDS" },
    ];

    @observable()
    @cachable
    filter: string;
    @cachable
    appliedFilter: string;
    initialFilters: QueryFilter[];

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.reloadFilters();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.reloadFilters();
        }
    }

    @computedFrom('rightshipMember')
    get rightshipMemberFilterActive() {
        return this.rightshipMember
    }

    @computedFrom(Reflection.getPropertyPath<PscActivities>(z => z.statusFilterCount))
    get statusFilterActive() {
        return this.statusFilterCount > 0;
    }

    @computedFrom('detentionFilterCount')
    get detentionFilterActive() {
        return this.detentionFilterCount > 0;
    }

    @computedFrom('myPscsFilterSelected')
    get myPSCsFilterActive() {
        return this.myPscsFilterSelected;
    }

    @computedFrom('filters', 'filters.length')
    get genericFiltersActive() {
        return this.filters.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PscActivities>(z => z.notInSafetyScoreFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.myPSCsFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.dataSourcesFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.genericFiltersActive),
        Reflection.getPropertyPath<PscActivities>(z => z.deficienciesFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.vesselAgeFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.mouFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.statusFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.detentionFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.rightshipMemberFilterActive),
        Reflection.getPropertyPath<PscActivities>(z => z.vesselStatusFilterActive))
    public get filtersSelected() {
        return this.notInSafetyScoreFilterActive || this.myPSCsFilterActive || this.dataSourcesFilterActive || this.genericFiltersActive ||
            this.deficienciesFilterActive || this.vesselAgeFilterActive || this.mouFilterActive || this.statusFilterActive ||
            this.detentionFilterActive || this.rightshipMemberFilterActive || this.vesselStatusFilterActive;
    }

    @cachable
    public myPscsFilterSelected: boolean = false;

    myPscsClick() {
        this.myPscsFilterSelected = !this.myPscsFilterSelected;

        this.reloadFilters();
    }

    clearFilters(reload: boolean = true) {
        if (this.dataGridVM) {
            this.dataGridVM.clearSorting();
        }
        this.filters = [];
        this.statusFilterIds = [];
        this.detentionFilterIds = [];
        this.rightshipMember = false;
        this.myPscsFilterSelected = false;
        this.deficienciesFirstFilter = this.deficienciesDefaults.low;
        this.deficienciesSecondFilter = this.deficienciesDefaults.high;
        this.showMouFilter = false;
        this.mouFilterIds = [];
        this.dataSourcesSelectedIds = [];
        this.startDate = undefined;
        this.endDate = undefined;
        this.vesselAgeFirstFilter = this.vesselAgeDefaults.low;
        this.vesselAgeSecondFilter = this.vesselAgeDefaults.high;
        this.mclFilters = {};
        this.selectedDocCompanies = [];
        this.dwtIsApplied = false;
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        
        this.selectedCountries = [];
        this.showCountryFilter = false;
        if (this.countryEntityFinderFilter) {
            this.countryEntityFinderFilter.clear();
        }
        else {
            this.clearCountryOnShow = true;
        }

        this.clearPortFilter();
        this.clearFilter(reload);
    }

    clearFilter(reload: boolean = true) {
        this.filter = null;
        this.appliedFilter = this.filter;
        if (reload)
            this.reloadFilters();
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 3 : 2,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: this.rightShipUser ? null : false,
                isHidden: true,
                name: 'submittedDate',
                title: 'SubmittedDate'
            },
            {
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.Number,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusRank',
                title: 'StatusRank'
            },
            {
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 2 : 3,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusDate',
                title: 'StatusDate'
            });
        return columns;
    }

    protected get odataUrl(): string {
        return "odata/Pscs";
    }

    public get pscInspectionStatusTypes() {
        return PscInspectionStatusEnum;
    }

    public filterTypes = PscFilters;

    //#region Default Filters
    @computedFrom("filters", "filters.length")
    public get isPendingSubtask(): boolean {
        return this.filters.indexOf(PscFilters.PendingSubtask) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get pendingVetFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.PendingVet) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get vettedLast12MthsFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.VettedLast12Mths) != -1;
    }

    @computedFrom("filters", "filters.length")
    public get discrepancyFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.Discrepancies) != -1;
    }

    @computedFrom("filters", "filters.length")
    public get notInSafetyScoreFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.NotInSafetyScore) != -1;
    }

    @computedFrom(Reflection.getPropertyPath<PscActivities>(z => z.detentionFilterCount))
    public get detentionFilterCountFilterActive() {
        return this.detentionFilterCount > 0;
    }

    //#endregion

    //#region Status Filters

    public get isStatusNone(): boolean {
        return !this.statusCreatedFilterActive
            && !this.statusCreatedFilterActive
            && !this.statusInProgressFilterActive
            && !this.statusAwaitingResponseFilterActive
            && !this.statusInspectionReassignmentFilterActive
            && !this.statusClosedFilterActive
            && !this.statusPendingReviewFilterActive
            && !this.statusMisreportedFilterActive;
    }

    @computedFrom("filters", "filters.length")
    public get statusMisreportedFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.Misreported) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get statusCreatedFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.Created) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get statusInProgressFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.InProgress) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get statusAwaitingResponseFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.AwaitingResponse) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get statusInspectionReassignmentFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.InspectionReassignment) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get statusClosedFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.Closed) !== -1;
    }

    @computedFrom("filters", "filters.length")
    public get statusPendingReviewFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.PendingReview) !== -1;
    }

    public getExcludedStatuses(): number[] {
        return [
            PscInspectionStatusEnum.PscInspectionReassignment
        ];
    }

    //#endregion

    protected get statusId(): PscInspectionStatusEnum {
        if (this.statusCreatedFilterActive) {
            return PscInspectionStatusEnum.Created;
        }

        if (this.statusInProgressFilterActive) {
            return PscInspectionStatusEnum.InProgress;
        }

        if (this.statusAwaitingResponseFilterActive) {
            return PscInspectionStatusEnum.AwaitingResponse;
        }

        if (this.statusInspectionReassignmentFilterActive) {
            return PscInspectionStatusEnum.PscInspectionReassignment;
        }

        if (this.statusClosedFilterActive) {
            return PscInspectionStatusEnum.Closed;
        }

        if (this.statusPendingReviewFilterActive) {
            return PscInspectionStatusEnum.PendingReview;
        }

        if (this.statusMisreportedFilterActive) {
            return PscInspectionStatusEnum.Misreported;
        }

        return null;
    }

    @computedFrom(Reflection.getPropertyName<PscActivities>(pa => pa.pscInspectionStatuses))
    public get filterNames() {
        const mappings: { [id: number]: string } = {};

        if (this.pscInspectionStatuses) {
            this.pscInspectionStatuses.forEach(lv => mappings[lv.id] = lv.name);
        }

        return mappings;
    }

    async init() {
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canViewSourceColumnInTasksPSC = this.userClaimsService.hasPermission(Claims.CanViewSourceColumnInTasksPSC)
        this.canAccessDueDiligence = this.userClaimsService.hasPermission(CommonUiClaims.CanAccessDueDiligence);
        const customerId = this.userClientService.currentUser.settings.customerId;
        if (customerId == null || customerId <= 0) {
            try {
                const c = await this.customerService.getCustomerId()
                this.isCustomer = c && c > 0;
            } catch (e) {
                console.warn(e);
            }
        }
        else this.isCustomer = true;
        this.columns = PscActivitiesColumns.getColumns(false, this.rightShipUser, this.isCustomer, this.userClaimsService);

        const requestPscStatuses = new LookupValueRequest();
        requestPscStatuses.lookupValueQuery = LookupValueQuery.PscInspectionStatuses;
        const x = await this.lookupClientService.getLookup(requestPscStatuses);

        const values = x.activeValues;

        if (this.isCustomer) {
            for (const a of values) {
                if (a.name == "Review Required")
                    a.name = "Pending Review";
            }
        }

        this.pscInspectionStatuses = Enumerable.from(values).orderBy(x => x.id).where(x => x.id != PscInspectionStatusEnum.PscInspectionReassignment).toArray();
        this.allPscInspectionStatuses = values;

        this.initialSorting = this.getSorting();

        if (this.applyCache()) {
            this.reloadFilters();
        } else {
            this.setDefaultFilters();
        }
    }

    nameConverterFunc: ((x) => string) = (name) => name == "Review Required" && this.isCustomer ? "Pending Review" : name;

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private signalClientService: SignalClientService, private lookupClientService: LookupClientService,
        private userClaimsService: UserClaimsService, private userClientService: UserClientService, private customerService: CommonCustomerService,
        private queryService: QueriesClientService,
        private notificationClientService: NotificationClientService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private dialogClientService: DialogClientService) {
        super(i18N, TasksConstants.PSCListName, bindingEngine);
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.companiesEntityTypes = this.entityTypeConfigurationHelper.getConfigurationByKey(["BasicCompanySearch"]);
        this.countryEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Countries"]);
    }

    async activate() {
        await this.init();
        this.refreshActivityCounts();
    }

    private setDefaultFilters() {
        this.filters = this.rightShipUser ? [PscFilters.PendingVet] : [];
        this.reloadFilters();
    }

    openMobileFilters() {
        this.isMobileFilterOpen = true;
    }

    attached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.PSC), this.refreshPscInspectionStatus);
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.PSC), this.refreshPscInspectionStatus);
        super.detached();
    }

    refreshPscInspectionStatus = async (param: EntityStatusChangeModel) => {
        if (this._isReloading) {
            console.log('refreshPscInspectionStatus is already in progress, skipping...');
            return;
        }

        try {
            if (this.dataGridVM) {
                this._isReloading = true;
                if (this.dataGridVM.page == 0) {
                    await this.dataGridVM.reloadData();
                } else {
                    const item = this.dataGridVM.itemsCollection.filter(x => x.item.pscInspectionId == param.entityId)[0];
                    if (item) {
                        item.item.statusId = param.statusId;
                        item.item.status = this.pscInspectionStatuses.filter(x => x.id == param.statusId).map(x => x.name)[0];
                    }
                }
                await this.refreshActivityCounts();
            }
        }
        finally {
            this._isReloading = false;
        }
    }

    async refreshActivityCounts() {
        if (this.userClaimsService.hasPermission(Claims.CanAccessPSCs)) {
            const x = await this.queryService.getData(`${this.odataUrl}/GetStatusCounts()`);

            this.clearCounts();

            for (let i = 0; i < x.keys.length; ++i) {
                const key = x.keys[i];
                const value = x.values[i];

                if (key == 'pendingVets') {
                    this.pendingVetCount = value;
                } else if (key == 'myPscs') {
                    this.myPscsCount = value;
                } else if (key == "discrepancies") {
                    this.discrepancyCount = value;
                } else if (key == "notInSafeScore") {
                    this.notInSafetyScoreCount = value;
                } else if (key == "rightShipMemberAndPriority") {
                    this.rightShipMemberAndPriorityCount = value;
                } else if (key == "pendingDueDiligence") {
                    this.pendingDueDiligenceCount = value;
                }
            }
        }
    }

    clearCounts() {
        this.pendingVetCount = 0;
        this.myPscsCount = 0;
        this.discrepancyCount = 0;
        this.notInSafetyScoreCount = 0;
        this.rightShipMemberAndPriorityCount = 0;
        this.pendingDueDiligenceCount = 0;
    }

    toggleStatusFilters() {
        this.showStatusFilter = !this.showStatusFilter;
    }

    public cachedMultipleLookups: { [lookup: string]: MultiLookupModel[] } = {};
    @cachable
    public mclFilters: { [lookup: string]: QueryFilter[] } = {};

    @cachable
    vesselTypeDescription: string;
    @cachable
    vesselStatusDisplayText: string;
    @cachable
    vesselStatusDescription: string;
    @cachable
    vesselTypeDisplayText: string;

    openMCLDialog = (type: string) => {
        let lookup = null;
        if (type == "vessel_types")
            lookup = LookupValueQuery.VesselTypes;
        else if (type == "vessel_statuses")
            lookup = LookupValueQuery.VesselStatuses;
        else {
            console.error("invalid multi filter type")
        }
        const displayText = type == "vessel_types" ? this.i18N.tr('vessel-type') : this.i18N.tr('vessel-status-text');

        this.closeAllFilters();

        const cachedMultiLookups = this.cachedMultipleLookups[lookup] ? this.cachedMultipleLookups[lookup] : RightShipMultiLookupModels.getMultiLookupModel(lookup);

        let filterValues: { [filterProperty: string]: number[] } = null;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0) {
            filterValues = {};
            this.mclFilters[lookup].forEach(x => {
                filterValues[x.filterProperty.name] = x.filterValue;
            });
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            multiLookups: cachedMultiLookups,
            title: displayText,
            selectedIds: filterValues,
            onApply: (multiLookups: MultiLookupModel[]) => {

                this.mclFilters[lookup] = [];

                const result = RightShipMultiLookupModels.getMultiLookupFilters(lookup, displayText, multiLookups);

                this.mclFilters[lookup] = result.filters;

                if (lookup == LookupValueQuery.VesselTypes) {
                    this.vesselTypeDisplayText = result.filterDisplayText;
                    this.vesselTypeDescription = result.filterDescription;
                }
                if (lookup == LookupValueQuery.VesselStatuses) {
                    this.vesselStatusDisplayText = result.filterDisplayText;
                    this.vesselStatusDescription = result.filterDescription;
                }

                this.cachedMultipleLookups[lookup] = multiLookups;

                this.reloadFilters();
            }
        }, true, false, false, 'right');
    }

    toggleDetentionFilters() {
        this.showDetentionFilter = !this.showDetentionFilter;
    }

    clearDetentionFilter(reload: boolean = true) {
        this.detentionFilterIds = [];
        if (reload) {
            this.reloadFilters();
        }
    }

    onStatusFilter = (event) => {
        this.statusFilterIds = event.filters.map(x => x.id);
        this.reloadFilters();
        this.showStatusFilter = false;
    }

    onDetentionFilter = (event) => {
        this.detentionFilterIds = event.filters.map(x => x.id);
        this.reloadFilters();
        this.showDetentionFilter = false;
    }

    onMouFilterChange = (event, reloadFilters: boolean = true) => {
        this.mouFilterIds = event.filters.map(x => x.id);
        this.showMouFilter = false;

        if (reloadFilters) {
            this.reloadFilters();
        }
    }

    onDateFilterChange = (event) => {
        if (this.startDate && this.endDate && this.startDate > this.endDate) {
            this.notificationClientService.show("Can't Apply Filter", "Start Date can't be greater than End Date", NotificationType.Error);
            return;
        }
        this.startDate = event.from; //two-way binding is a bit delayed when Clear is clicked
        this.endDate = event.to;    //so we set the value to make sure it is up to date
        this.showDateFilter = false;
        this.reloadFilters();
    }

    public setFilter(filter: PscFilters) {
        if (!this.filters)
            this.filters = [];

        const index = this.filters.indexOf(filter);
        if (index > -1)
            this.filters.splice(index, 1);
        else
            this.filters.push(filter);

        this.reloadFilters();
    }

    @computedFrom('statusFilterIds', 'statusFilterIds.length')
    public get statusFilterCount() {
        return this.statusFilterIds?.length ?? 0;
    }

    @computedFrom('detentionFilterIds', 'detentionFilterIds.length')
    public get detentionFilterCount() {
        return this.detentionFilterIds?.length ?? 0;
    }

    @computedFrom('mouFilterIds', 'mouFilterIds.length')
    public get mouFilterActive() {
        return this.mouFilterIds?.length > 0;
    }

    @computedFrom(Reflection.getPropertyPath<PscActivities>(z => z.startDate),
        Reflection.getPropertyPath<PscActivities>(z => z.endDate))
    public get dateFilterActive() {
        return this.startDate || this.endDate;
    }

    rightshipMemberClick() {
        this.rightshipMember = !this.rightshipMember;
        this.reloadFilters();
    }

    public reloadFilters() {
        if (this.columns?.length <= 0) {
            this.columns = PscActivitiesColumns.getColumns(false, this.rightShipUser, this.isCustomer, this.userClaimsService);
        }

        let initialFilters = [];

        if (this.myPscsFilterSelected) {
            if (this.isCustomer) {
                initialFilters.push(QueryFilter.getQuery("subtaskCloseoutUserId", this.userClientService.currentUser.id, "eq", DataType.Number, true));
            } else {
                initialFilters.push(QueryFilter.getQuery("reviewrId", this.userClientService.currentUser.id, "eq", DataType.Number, true));

                initialFilters.push(QueryFilter.getQuery("statusId", [
                    PscInspectionStatusEnum.Created,
                    PscInspectionStatusEnum.InProgress,
                    PscInspectionStatusEnum.AwaitingResponse,
                    PscInspectionStatusEnum.PendingReview,
                    PscInspectionStatusEnum.PscInspectionReassignment,
                ], "{0} in ({1})", DataType.Number, true, false, null, false, true));
            }
        }

        if (this.isPendingSubtask && !this.isCustomer) {
            const guidPendingSubtask = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("statusId", 4, "ne", DataType.Number, true, false, guidPendingSubtask, true));
            initialFilters.push(QueryFilter.getQuery("closed", false, "eq", DataType.Boolean, true, false, guidPendingSubtask, true));
            const filter = QueryFilter.getQuery("pendingSubtaskEntityId", null, "ne", DataType.Number, true, false, guidPendingSubtask, true);
            filter.skipOnNullOrEmpty = false;
            initialFilters.push(filter);
        }

        if (this.pendingVetFilterActive && !this.isCustomer) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("hasVet", this.pendingVetFilterActive, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.pendingDueDiligenceFilterActive && this.dueDiligenceFilterVisible) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("hasDueDiligence", this.pendingDueDiligenceFilterActive, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.vettedLast12MthsFilterActive && this.rightShipUser) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("hasVetsInLast12Months", this.vettedLast12MthsFilterActive, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.discrepancyFilterActive && !this.isCustomer) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("statusId", [
                PscInspectionStatusEnum.Misreported,
                PscInspectionStatusEnum.Closed
            ], "{0} in ({1}) eq false", DataType.Number, true, false, guid, true, true));
            const filter = QueryFilter.getQuery("dataSourceConflictingDataPoints", null, "{0} ne {1}", DataType.Default, true, true, guid, true, true);
            filter.skipOnNullOrEmpty = false;
            initialFilters.push(filter);
        }

        //#region vessel status filter
        if (this.mclFilters.RS_VesselStatuses && this.mclFilters.RS_VesselStatuses.length > 0) {
            initialFilters = initialFilters.concat(this.mclFilters.RS_VesselStatuses)
        }
        //#endregion

        //#region vessel type filter
        if (this.mclFilters.RS_VesselTypes && this.mclFilters.RS_VesselTypes.length > 0) {
            initialFilters = initialFilters.concat(this.mclFilters.RS_VesselTypes)
        }
        //#endregion

        if (this.notInSafetyScoreFilterActive && !this.isCustomer) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("usedInSafetyScore", true, "ne", DataType.Boolean, true, false, guid, true));
            initialFilters.push(QueryFilter.getQuery("followUpInspection", true, "ne", DataType.Boolean, true, false, guid, true));
            initialFilters.push(QueryFilter.getQuery("statusId", PscInspectionStatusEnum.Misreported, "ne", DataType.Number, true, true, guid, true));
        }

        if (this.statusFilterIds?.length > 0) {
            const statusIdFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "statusId",
                    isDynamicProperty: false,
                    type: DataType.Lookup,
                    lookupMappedToProperty: "RS_PscInspectionStatuses",
                    isNullable: true,
                },
                filterValue: this.statusFilterIds || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(statusIdFilter);
        }

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("vesselName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            initialFilters.push(QueryFilter.getQuery("vesselImo", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            initialFilters.push(QueryFilter.getQuery("pscIdStr", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        if (this.rightshipMember && !this.isCustomer) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("isRightShipMember", true, "eq", DataType.Boolean, true, false, guid, true));
            initialFilters.push(QueryFilter.getQuery("isPriority", true, "eq", DataType.Boolean, false, true, guid, true));
        }

        if (this.detentionFilterIds?.length > 0) {
            const guid = Guid.generateGuid();
            if (this.detentionFilterIds.indexOf(1) !== -1 && this.detentionFilterIds.indexOf(2) === -1) {
                initialFilters.push(QueryFilter.getQuery("shipDetained", true, "eq", DataType.Boolean, true, false, guid, true));
            }
            if (this.detentionFilterIds.indexOf(2) !== -1 && this.detentionFilterIds.indexOf(1) === -1) {
                initialFilters.push(QueryFilter.getQuery("shipDetained", false, "eq", DataType.Boolean, true, false, guid, true));
            }
        }

        if (this.deficienciesFilterActive) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("numberOfDeficiencies", this.deficienciesFirstFilter, "ge", DataType.Number, true, false, guid, true, false));
            initialFilters.push(QueryFilter.getQuery("numberOfDeficiencies", this.deficienciesSecondFilter, "le", DataType.Number, true, true, guid, true, false));
        }

        if (this.vesselAgeFilterActive) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("vesselAge", this.vesselAgeFirstFilter, "ge", DataType.Number, true, false, guid, true, false));
            initialFilters.push(QueryFilter.getQuery("vesselAge", this.vesselAgeSecondFilter, "le", DataType.Number, true, true, guid, true, false));
        }

        if (this.dateFilterActive) {
            const guid = Guid.generateGuid();
            if (this.startDate)
                initialFilters.push(QueryFilter.getQuery("inspectionDate", this.startDate, "ge", DataType.Date, true, false, guid, true, false));
            if (this.endDate)
                initialFilters.push(QueryFilter.getQuery("inspectionDate", this.endDate, "le", DataType.Date, true, true, guid, true, false));
        }

        if (this.mouFilterActive) {
            initialFilters.push(QueryFilter.getQuery("pscAuthorityId", this.mouFilterIds, "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }

        if (this.dataSourcesFilterActive) {
            const guid = Guid.generateGuid();
            this.dataSourcesSelectedIds.forEach((id, index) => {
                const ds = this.dataSources.find(x => x.id == id);
                initialFilters.push(QueryFilter.getQuery(`hasDataSource_${ds.code}`, true, "eq", DataType.Boolean, index == 0, (this.dataSourcesSelectedIds.length - 1) == index, guid, true, false));
            });
        }

        if (this.portFilterActive) {
            initialFilters.push(QueryFilter.getQuery("portId", this.selectedPorts, "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }
        
        //#region doc company
        if (this.docCompanyFilterActive) {

            const docCompanyFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "docCompanyId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedDocCompanies || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(docCompanyFilter);
        }
        //#endregion

        //#region dwt
        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }
        //#endregion

        if (this.countryFilterActive) {

            const countryFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "countryId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCountries || [],
                isAnd: true,
                skipOnNullOrEmpty: true
            };

            initialFilters.push(countryFilter);
        }

        this.initialFilters = initialFilters;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl,
            columns: this.columns,
            initialFilters: initialFilters,
            initialSorting: this.initialSorting
        };
    }

    closeAllFilters() {
        this.showDeficienciesFilter = false;
        this.showMouFilter = false;
        this.showDataSourceFilter = false;
        this.showDetentionFilter = false;
        this.showStatusFilter = false;
        this.showDocCompanyFilter = false;
        this.showDwtFilter = false;
    }

    clearStatusFilter(reload: boolean = true) {
        this.statusFilterIds = [];

        if (reload) {
            this.reloadFilters();
        }
    }

    //#region Deficiencies Filter
    showDeficienciesFilter: boolean = false;
    deficienciesDefaults: { low: number, high: number; } = { low: 0, high: 100 };
    @cachable
    deficienciesFirstFilter: number;
    @cachable
    deficienciesSecondFilter: number;

    toggleDeficienciesFilters() {
        this.showDeficienciesFilter = !this.showDeficienciesFilter;
    }

    //#region RS Port filter
    public portEntityFinderFilter: EntityFinderSearchFilter;
    public showPortFilter: boolean = false;
    @cachable
    public selectedPorts: number[] = [];
    public clearPortOnShow: boolean;

    public togglePortFilters() {
        if (!this.showPortFilter) {
            this.closeAllFilters();
        }
        this.showPortFilter = !this.showPortFilter;
    }

    public applyPortFilter(items: any[], reload: boolean = true) {
        this.selectedPorts = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.togglePortFilters();
    }

    public clearPortFilter() {
        this.selectedPorts = [];
        if (this.portEntityFinderFilter) {
            this.portEntityFinderFilter.clear();
        }
        else {
            this.clearPortOnShow = true;
        }
        this.reloadFilters();
    }

    @computedFrom("selectedPorts", "selectedPorts.length")
    public get portFilterActive() {
        return this.selectedPorts && this.selectedPorts.length > 0;
    }
    //#endregion

    //#region vessel type filter

    clearVesselTypeFilter(reload: boolean = true) {
        const lookup = LookupValueQuery.VesselTypes;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselTypeDescription = "";
        this.vesselTypeDisplayText = this.i18N.tr('vessel-type');
        if (reload)
            this.reloadFilters();
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselTypes", "mclFilters.RS_VesselTypes.length")
    public get vesselTypeFilterActive() {
        const lookup = LookupValueQuery.VesselTypes;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    //#region vesselAge Filter
    showVesselAgeFilter: boolean = false;
    vesselAgeDefaults: { low: number, high: number; } = { low: 0, high: 100 };
    @cachable
    vesselAgeFirstFilter: number;
    @cachable
    vesselAgeSecondFilter: number;

    toggleVesselAgeFilters() {
        this.showVesselAgeFilter = !this.showVesselAgeFilter;
    }

    toggleMouFilters() {
        this.showMouFilter = !this.showMouFilter;
    }

    public mobileApplySelectedFilter() {
        // Do nothing
    }

    public mobileFiltersApply() {
        this.closeMobileFiltersDialog();
        this.reloadFilters();
    }

    clearMouFilters(reload: boolean = true) {
        this.mouFilterIds = [];

        if (reload) {
            this.reloadFilters();
        }
    }

    applyDeficienciesFilter(reload: boolean = true) {
        if (reload) {
            this.reloadFilters();

            this.showDeficienciesFilter = false;
        }
    }

    applyVesselAgeFilter(reload: boolean = true) {
        if (reload) {
            this.reloadFilters();

            this.showVesselAgeFilter = false;
        }
    }

    @computedFrom(Reflection.getPropertyPath<PscActivities>(z => z.deficienciesFirstFilter),
        Reflection.getPropertyPath<PscActivities>(z => z.deficienciesSecondFilter),
        Reflection.getPropertyPath<PscActivities>(z => z.deficienciesDefaults.low),
        Reflection.getPropertyPath<PscActivities>(z => z.deficienciesDefaults.high))
    public get deficienciesFilterActive() {
        return this.deficienciesFirstFilter > this.deficienciesDefaults.low ||
            this.deficienciesSecondFilter < this.deficienciesDefaults.high;
    }

    clearDeficienciesFilter(reload: boolean = true) {
        this.deficienciesFirstFilter = this.deficienciesDefaults.low;
        this.deficienciesSecondFilter = this.deficienciesDefaults.high;

        if (reload) {
            this.reloadFilters();
        }
    }

    @computedFrom(Reflection.getPropertyPath<PscActivities>(z => z.vesselAgeFirstFilter),
        Reflection.getPropertyPath<PscActivities>(z => z.vesselAgeSecondFilter),
        Reflection.getPropertyPath<PscActivities>(z => z.vesselAgeDefaults.low),
        Reflection.getPropertyPath<PscActivities>(z => z.vesselAgeDefaults.high))
    public get vesselAgeFilterActive() {
        return this.vesselAgeFirstFilter > this.vesselAgeDefaults.low ||
            this.vesselAgeSecondFilter < this.vesselAgeDefaults.high;
    }

    clearVesselAgeFilter(reload: boolean = true) {
        this.vesselAgeFirstFilter = this.vesselAgeDefaults.low;
        this.vesselAgeSecondFilter = this.vesselAgeDefaults.high;

        if (reload) {
            this.reloadFilters();
        }
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselStatuses", "mclFilters.RS_VesselStatuses.length")
    public get vesselStatusFilterActive() {
        const lookup = LookupValueQuery.VesselStatuses;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    //#region Data Source Filter
    showDataSourceFilter: boolean = false;
    @cachable
    dataSourcesSelectedIds: number[] = [];

    @computedFrom('dataSourcesSelected', 'dataSourcesSelected.length', 'dataSourcesSelectedIds', 'dataSourcesSelectedIds.length')
    public get dataSourcesFilterActive() {
        return (this.dataSourcesSelectedIds ? this.dataSourcesSelectedIds.length > 0 : false);
    }

    toggleDataSourceFilters() {
        this.showDataSourceFilter = !this.showDataSourceFilter;
    }

    applyDataSourceFilter(event) {
        this.dataSourcesSelectedIds = event.filters.map(x => x.id);
        this.reloadFilters();
        this.showDataSourceFilter = false;
    }

    clearDataSourcesFilter(reload: boolean = true) {
        this.dataSourcesSelectedIds = [];

        if (reload) {
            this.reloadFilters();
        }
    }

    //#endregion

    //#region doc company filter
    public showDocCompanyFilter: boolean = false;
    @cachable
    public selectedDocCompanies: number[] = [];

    public toggleDocCompanyFilters() {
        if (!this.showDocCompanyFilter) {
            this.closeAllFilters();
        }
        this.showDocCompanyFilter = !this.showDocCompanyFilter;
    }

    public applyDocCompanyFilter(items: any[], reload: boolean = true) {
        if (!items) {
            return;
        }
        this.selectedDocCompanies = items.map(x => x.id);
        if (reload == true) this.reloadFilters();
        this.toggleDocCompanyFilters();
    }

    public clearDocCompanyFilter() {
        this.selectedDocCompanies = [];
        this.reloadFilters();
    }

    @computedFrom("selectedDocCompanies", "selectedDocCompanies.length")
    public get docCompanyFilterActive() {
        return this.selectedDocCompanies?.length > 0 && this.selectedDocCompanies[0] != null;
    }

    //#endregion

    //#region dwt filter
    @cachable
    public dwtFirstFilter: number;
    @cachable
    public dwtSecondFilter: number;
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public showDwtFilter: boolean = false;
    @cachable
    public dwtIsApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high;
            this.dwtIsApplied = true;
        }

        this.reloadFilters();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("dwtIsApplied", "dwtFirstFilter", "dwtSecondFilter")
    public get dwtFilterActive() {
        return this.dwtIsApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= this.dwtDefaults.low) || (this.dwtSecondFilter && this.dwtSecondFilter <= this.dwtDefaults.high));
    }

    //#endregion

    //#region due diligence filter
    
    public pendingDueDiligenceCount: number = 0;
    public canAccessDueDiligence: boolean;
    
    @computedFrom(Reflection.getPropertyPath<PscActivities>(z => z.canAccessDueDiligence),
        Reflection.getPropertyPath<PscActivities>(z => z.rightShipUser))
    public get dueDiligenceFilterVisible(): boolean
    {
        return this.canAccessDueDiligence && this.rightShipUser;
    }

    @computedFrom("filters", "filters.length")
    public get pendingDueDiligenceFilterActive(): boolean {
        return this.filters.indexOf(PscFilters.PendingDueDiligence) !== -1;
    }

    //#endregion

    hideMobileFilters() {
        this.closeMobileFiltersDialog();
    }

    closeMobileFiltersDialog() {
        this.isMobileFilterOpen = false;
    }
}
